// src/components/listing-leads/NewListingLeadModal.jsx
// Simple add-listing-lead modal. Unlike the buyer modal it does NOT require a
// Gmail-connected client — sellers are captured directly by name + property.
import React, { useRef, useState } from "react";
import { X, Check } from "lucide-react";
import { toast } from "react-toastify";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import { useStateValue } from "../../context/StateProvider";
import AddClientPicker from "../new-contact/AddClientPicker";
import ContactEditDrawer from "../new-contact/ContactEditDrawer";
import { saveListingLead, getContactsByIds } from "../../utils/firebasefunctions";
import { addContact } from "../../utils/contactsApi";
import { normalizeContact } from "../../utils/contactNormalize";
import { addCoClient, removeClient, listClients } from "../../utils/leadClients";
import { LISTING_STAGES } from "../../utils/listingLeadStages";
import { systemSourceOptions } from "../../utils/leadSourceLabel";
import { LISTING_FORM_TYPES } from "../../utils/portalForms";
import { requestSellerFile } from "../../utils/sellerFileRequest";
import { sendSellerPortalForms } from "../../utils/sendSellerPortalForms";
import {
  formatNumber,
  removeCommas,
  stripCountry,
  notifyOnNewLead,
} from "../../utils/helper";
import { excludeTestAgents } from "../../utils/testAgents";

const RATINGS = ["A", "B", "C"];
// Same key + libraries as the Add Deal form — the loader requires identical
// options everywhere it's used.
const GMAPS_LIBRARIES = ["places"];

export default function NewListingLeadModal({ onClose }) {
  const [{ user, users, employees, leadsource }] = useStateValue();
  // Google Places autocomplete on the Property Address field — same behavior
  // as the Add Deal form (pick a suggestion → full formatted address fills in).
  const { isLoaded: placesReady } = useLoadScript({
    googleMapsApiKey: "AIzaSyClMaFJLo3buNYK-siytTa6AWD_72TgrE8",
    libraries: GMAPS_LIBRARIES,
  });
  const addressAcRef = useRef(null);
  const onPlacePicked = () => {
    const place = addressAcRef.current?.getPlace();
    if (place?.formatted_address) {
      setForm((f) => ({
        ...f,
        propertyAddress: stripCountry(place.formatted_address),
      }));
    }
  };
  const [form, setForm] = useState({
    contactId: "",
    contactName: "",
    assignedAgent: user?.id || "",
    assignedAgentName:
      [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.name || "",
    coClients: [],
    propertyAddress: "",
    estValue: "",
    source: "",
    rating: "",
    stage: "New Lead",
    appointmentAt: "",
    nextFollowUp: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [coPickerOpen, setCoPickerOpen] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);
  const [creatingFor, setCreatingFor] = useState("primary");
  const [requestFile, setRequestFile] = useState(false);
  const [formType, setFormType] = useState("seller");
  const [sendForms, setSendForms] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Privileged roles (Team Lead / Client Care) can create a lead on behalf of an
  // agent; a regular agent just gets themselves. The lead registers under the
  // chosen agent. Ensure the current user is always a selectable option.
  const isPrivileged = user?.role === "Admin" || user?.role === "Supervisor";
  const agentOptions = (() => {
    const list = excludeTestAgents(employees || []);
    if (user?.id && !list.some((e) => e.id === user.id)) {
      return [{ id: user.id, firstname: user.firstname, lastname: user.lastname, email: user.email }, ...list];
    }
    return list;
  })();

  // Pick an existing contact as the seller (mirrors the buyer modal).
  const attachContact = async (contactId) => {
    setPickerOpen(false);
    try {
      const [raw] = await getContactsByIds([contactId]);
      const c = normalizeContact(raw);
      setForm((f) => ({ ...f, contactId, contactName: c?.name || "" }));
    } catch (e) {
      setForm((f) => ({ ...f, contactId }));
    }
  };

  const attachCoClient = async (contactId) => {
    setCoPickerOpen(false);
    try {
      const [raw] = await getContactsByIds([contactId]);
      const c = normalizeContact(raw);
      setForm((f) => addCoClient(f, { contactId, contactName: c?.name || "" }));
    } catch (e) {
      setForm((f) => addCoClient(f, { contactId, contactName: "" }));
    }
  };

  const handleCreatedContact = async (formContact) => {
    if (!user?.gmailtoken?.access_token) {
      toast.error("Connect Gmail in Settings before adding a new contact");
      return;
    }
    try {
      const created = await addContact({ user, contact: formContact });
      setCreatingContact(false);
      if (created?.firebaseId) {
        if (creatingFor === "co") await attachCoClient(created.firebaseId);
        else await attachContact(created.firebaseId);
      }
    } catch (e) {
      toast.error(e.message || "Failed to create contact");
    }
  };

  const submit = async () => {
    if (!form.contactId) {
      toast.warn("Please select a client first");
      return;
    }
    setSaving(true);
    const agentName =
      [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.name || "";
    // Register under the chosen agent (defaults to self); keep enteredBy for audit.
    const ownerId = form.assignedAgent || user?.id || "";
    const ownerName = form.assignedAgentName || agentName;
    const res = await saveListingLead({
      ...form,
      estValue: form.estValue ? Number(String(form.estValue).replace(/[^0-9.]/g, "")) : null,
      assignedAgent: ownerId,
      assignedAgentName: ownerName,
      createdby: ownerId,
      enteredBy: user?.id || "",
    });
    if (!res.success) {
      setSaving(false);
      toast.error(res.error || "Failed to save lead");
      return;
    }
    notifyOnNewLead(res.lead, users, "seller").catch(() => {});

    if (requestFile) {
      // Mint the hidden listing file (→ Drive folder), carrying the chosen
      // Form Type so the portal assembles the property-type-tailored forms;
      // then optionally email the seller their forms.
      const savedLead = {
        ...form,
        id: res.lead.id,
        assignedAgent: ownerId,
        assignedAgentName: ownerName,
      };
      try {
        const { deal } = await requestSellerFile(savedLead, user, {
          sendForms,
          formType,
        });
        if (sendForms && deal) {
          const sent = await sendSellerPortalForms(deal, savedLead, user);
          if (!sent.ok && sent.reason)
            toast.info(`File created. Forms not sent: ${sent.reason}.`);
        }
      } catch (e) {
        console.error("Seller file creation failed:", e);
        toast.error(
          "Lead saved, but building the file failed — you can Request a File from the lead.",
        );
      }
    }

    setSaving(false);
    toast.success(
      requestFile ? "Listing Lead created · file requested" : "Listing Lead created",
    );
    onClose();
  };

  const field = "w-full mt-1 bg-slate-50 border border-slate-200 px-3 py-2 text-sm rounded-lg";
  const label = "block text-xs font-medium text-slate-500";

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-40 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[88vh] overflow-hidden flex flex-col pointer-events-auto">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <h3 className="text-base font-semibold text-slate-900">Add Listing Lead</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            <div>
              <label className={label}>Client (Seller) *</label>
              {form.contactId ? (
                <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mt-1">
                  <span className="text-sm font-medium text-slate-800">{form.contactName || "Selected contact"}</span>
                  <button className="text-xs text-indigo-600" onClick={() => setPickerOpen(true)}>Change</button>
                </div>
              ) : (
                <button onClick={() => setPickerOpen(true)}
                  className="w-full mt-1 border border-dashed border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-600 hover:border-slate-400 text-left">
                  + Select or Create a Client
                </button>
              )}
            </div>
            {form.contactId && (
              <div>
                <label className={label}>Co-Clients</label>
                {listClients(form)
                  .filter((c) => !c.primary)
                  .map((c) => (
                    <div key={c.contactId} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mt-1">
                      <span className="text-sm text-slate-700">{c.contactName || "Selected contact"}</span>
                      <button className="text-xs text-slate-400 hover:text-red-500"
                        onClick={() => setForm((f) => removeClient(f, c.contactId))}>Remove</button>
                    </div>
                  ))}
                <button onClick={() => setCoPickerOpen(true)}
                  className="w-full mt-1 border border-dashed border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-600 hover:border-slate-400 text-left">
                  + Add Co-Client
                </button>
              </div>
            )}
            <div>
              <label className={label}>Property Address</label>
              {placesReady ? (
                <Autocomplete
                  onLoad={(ref) => (addressAcRef.current = ref)}
                  onPlaceChanged={onPlacePicked}
                >
                  <input className={field} value={form.propertyAddress} onChange={set("propertyAddress")} placeholder="1423 Oak St" />
                </Autocomplete>
              ) : (
                <input className={field} value={form.propertyAddress} onChange={set("propertyAddress")} placeholder="1423 Oak St" />
              )}
            </div>
            <div className="flex gap-3">
              <div className="w-1/2">
                <label className={label}>Estimated Value</label>
                <input
                  className={field}
                  value={formatNumber(form.estValue)}
                  onChange={(e) => {
                    const raw = removeCommas(e.target.value).replace(/[^0-9]/g, "");
                    setForm((f) => ({ ...f, estValue: raw }));
                  }}
                  placeholder="$620,000"
                />
              </div>
              <div className="w-1/2">
                <label className={label}>Source</label>
                <select className={field} value={form.source} onChange={set("source")}>
                  <option value="">—</option>
                  {systemSourceOptions(leadsource).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-1/2">
                <label className={label}>Rating</label>
                <select className={field} value={form.rating} onChange={set("rating")}>
                  <option value="">—</option>
                  {RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="w-1/2">
                <label className={label}>Stage</label>
                <select className={field} value={form.stage} onChange={set("stage")}>
                  {LISTING_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-1/2">
                <label className={label}>Listing Appt Date</label>
                <input type="date" className={field} value={form.appointmentAt} onChange={set("appointmentAt")} />
              </div>
              <div className="w-1/2">
                <label className={label}>Next Follow-Up</label>
                <input type="date" className={field} value={form.nextFollowUp} onChange={set("nextFollowUp")} />
              </div>
            </div>
            {isPrivileged && (
              <div>
                <label className={label}>Assigned Agent (On Behalf Of)</label>
                <select
                  className={field}
                  value={form.assignedAgent}
                  onChange={(e) => {
                    const a = agentOptions.find((x) => x.id === e.target.value);
                    setForm((f) => ({
                      ...f,
                      assignedAgent: e.target.value,
                      assignedAgentName: a
                        ? [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.email || ""
                        : "",
                    }));
                  }}
                >
                  {agentOptions.map((a) => (
                    <option key={a.id} value={a.id}>
                      {([a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.email) +
                        (a.id === user?.id ? " (Me)" : "")}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className={label}>Notes</label>
              <textarea className={field} rows={2} value={form.notes} onChange={set("notes")} />
            </div>

            <div className="border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={() => setRequestFile((v) => !v)}
                className={`w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold border transition ${
                  requestFile
                    ? "bg-[#1E293B] border-[#1E293B] text-white"
                    : "bg-white border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                }`}
              >
                <Check size={16} className={requestFile ? "" : "opacity-40"} />
                Also Request a File for This Listing
              </button>
              <p className="text-[11px] text-slate-400 mt-1.5 text-center">
                Creates the listing file + Drive folder and sends it to Pending File Creation.
              </p>

              {requestFile && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className={label}>Form Type</label>
                    <select
                      className={field}
                      value={formType}
                      onChange={(e) => setFormType(e.target.value)}
                    >
                      {LISTING_FORM_TYPES.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Picks which forms the seller fills out (Condo adds Association + Unit, Pool adds Pool, etc.).
                    </p>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => setSendForms((v) => !v)}
                      className={`w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold border transition ${
                        sendForms
                          ? "bg-[#1E293B] border-[#1E293B] text-white"
                          : "bg-white border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                      }`}
                    >
                      <Check size={16} className={sendForms ? "" : "opacity-40"} />
                      Also Send Seller Forms
                    </button>
                    <p className="text-[11px] text-slate-400 mt-1.5 text-center">
                      Emails this seller a link to their listing forms — even before signing.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-600">Cancel</button>
            <button onClick={submit} disabled={saving}
              className="px-5 py-2 text-sm rounded-lg bg-[#1E293B] text-white font-semibold disabled:opacity-60">
              {saving ? "Saving…" : "Create Lead"}
            </button>
          </div>
        </div>
      </div>

      {pickerOpen && (
        <AddClientPicker
          userId={user.id}
          existingCustomerIds={listClients(form).map((c) => c.contactId)}
          onClose={() => setPickerOpen(false)}
          onPick={attachContact}
          onCreateNew={() => { setPickerOpen(false); setCreatingFor("primary"); setCreatingContact(true); }}
        />
      )}
      {coPickerOpen && (
        <AddClientPicker
          userId={user.id}
          existingCustomerIds={listClients(form).map((c) => c.contactId)}
          onClose={() => setCoPickerOpen(false)}
          onPick={attachCoClient}
          onCreateNew={() => { setCoPickerOpen(false); setCreatingFor("co"); setCreatingContact(true); }}
        />
      )}
      {creatingContact && (
        <ContactEditDrawer
          onClose={() => setCreatingContact(false)}
          onSubmit={handleCreatedContact}
        />
      )}
    </>
  );
}
